import { useCallback } from "react";
import { createRoot } from "react-dom/client";
import { flushSync } from "react-dom";
import html2pdf from "html2pdf.js";
import PropertyBrochureTemplate from "./PropertyBrochureTemplate";
import { PropertyBrochureData } from "./propertyBrochure.types";
import { ThemeProvider } from "@mui/material/styles";
import theme from "@/shared/theme/theme";
import { Provider } from "react-redux";
import { store } from "@/src-refactor/modules/store/store";

export type { PropertyBrochureData };

const UNSUPPORTED_COLOR_FUNCTION = "oklch(";

const createColorNormalizer = () => {
  const canvas = document.createElement("canvas");
  canvas.width = 1;
  canvas.height = 1;
  const context = canvas.getContext("2d", { willReadFrequently: true });
  const cache = new Map<string, string>();

  return (colorValue: string): string => {
    if (!context || !colorValue.includes(UNSUPPORTED_COLOR_FUNCTION)) {
      return colorValue;
    }

    const cached = cache.get(colorValue);
    if (cached) {
      return cached;
    }

    try {
      context.clearRect(0, 0, 1, 1);
      context.fillStyle = "rgba(0, 0, 0, 0)";
      context.fillStyle = colorValue;
      context.fillRect(0, 0, 1, 1);

      const [red, green, blue, alpha] = context.getImageData(0, 0, 1, 1).data;
      const normalized = `rgba(${red}, ${green}, ${blue}, ${(alpha / 255).toFixed(3)})`;
      cache.set(colorValue, normalized);
      return normalized;
    } catch {
      return colorValue;
    }
  };
};

const sanitizeUnsupportedColors = (root: HTMLElement, doc: Document) => {
  const view = doc.defaultView;
  if (!view) {
    return;
  }

  const normalizeColor = createColorNormalizer();
  const styleColorProperties = [
    "color",
    "background-color",
    "border-top-color",
    "border-right-color",
    "border-bottom-color",
    "border-left-color",
    "outline-color",
    "text-decoration-color",
    "caret-color"
  ] as const;

  const elements = [root, ...Array.from(root.querySelectorAll<HTMLElement>("*"))];
  const pseudoRuleById = new Map<string, string[]>();
  let colorFixId = 0;

  elements.forEach(currentElement => {
    const computedStyle = view.getComputedStyle(currentElement);

    styleColorProperties.forEach(propertyName => {
      const propertyValue = computedStyle.getPropertyValue(propertyName);
      if (!propertyValue.includes(UNSUPPORTED_COLOR_FUNCTION)) {
        return;
      }

      currentElement.style.setProperty(
        propertyName,
        normalizeColor(propertyValue),
        "important"
      );
    });

    const fill = computedStyle.getPropertyValue("fill");
    if (fill.includes(UNSUPPORTED_COLOR_FUNCTION)) {
      currentElement.style.setProperty("fill", normalizeColor(fill), "important");
    }

    const stroke = computedStyle.getPropertyValue("stroke");
    if (stroke.includes(UNSUPPORTED_COLOR_FUNCTION)) {
      currentElement.style.setProperty("stroke", normalizeColor(stroke), "important");
    }

    const pseudoElements = ["::before", "::after"] as const;
    pseudoElements.forEach(pseudoElement => {
      const pseudoStyle = view.getComputedStyle(currentElement, pseudoElement);
      const pseudoDeclarations: string[] = [];

      styleColorProperties.forEach(propertyName => {
        const propertyValue = pseudoStyle.getPropertyValue(propertyName);
        if (!propertyValue.includes(UNSUPPORTED_COLOR_FUNCTION)) {
          return;
        }

        pseudoDeclarations.push(
          `${propertyName}: ${normalizeColor(propertyValue)} !important;`
        );
      });

      const pseudoFill = pseudoStyle.getPropertyValue("fill");
      if (pseudoFill.includes(UNSUPPORTED_COLOR_FUNCTION)) {
        pseudoDeclarations.push(`fill: ${normalizeColor(pseudoFill)} !important;`);
      }

      const pseudoStroke = pseudoStyle.getPropertyValue("stroke");
      if (pseudoStroke.includes(UNSUPPORTED_COLOR_FUNCTION)) {
        pseudoDeclarations.push(`stroke: ${normalizeColor(pseudoStroke)} !important;`);
      }

      if (!pseudoDeclarations.length) {
        return;
      }

      if (!currentElement.dataset.h2cColorFixId) {
        colorFixId += 1;
        currentElement.dataset.h2cColorFixId = `${colorFixId}`;
      }

      const elementRules =
        pseudoRuleById.get(currentElement.dataset.h2cColorFixId) ?? [];
      elementRules.push(
        `[data-h2c-color-fix-id="${currentElement.dataset.h2cColorFixId}"]${pseudoElement}{${pseudoDeclarations.join(
          ""
        )}}`
      );
      pseudoRuleById.set(currentElement.dataset.h2cColorFixId, elementRules);
    });
  });

  if (pseudoRuleById.size) {
    const pseudoFixStyle = doc.createElement("style");
    pseudoFixStyle.setAttribute("data-html2canvas-color-fix", "true");
    pseudoFixStyle.textContent = Array.from(pseudoRuleById.values())
      .flat()
      .join("\n");
    doc.head.appendChild(pseudoFixStyle);
  }
};

const sanitizeUnsupportedColorsInClone = (clonedDocument: Document) => {
  const htmlElement = clonedDocument.documentElement as HTMLElement | null;
  const bodyElement = clonedDocument.body as HTMLElement | null;
  const brochureElement = clonedDocument.querySelector(
    "#property-brochure"
  ) as HTMLElement | null;

  if (htmlElement) {
    sanitizeUnsupportedColors(htmlElement, clonedDocument);
  }

  if (bodyElement) {
    sanitizeUnsupportedColors(bodyElement, clonedDocument);
  }

  if (brochureElement) {
    sanitizeUnsupportedColors(brochureElement, clonedDocument);
  }

  if (bodyElement) {
    const view = clonedDocument.defaultView;
    if (view) {
      const computedBodyBackground = view
        .getComputedStyle(bodyElement)
        .getPropertyValue("background-color");
      if (computedBodyBackground.includes(UNSUPPORTED_COLOR_FUNCTION)) {
        bodyElement.style.setProperty(
          "background-color",
          createColorNormalizer()(computedBodyBackground),
          "important"
        );
      }
    }
  }
};

const usePropertyBrochurePdf = () => {
  const generateBrochure = useCallback(
    async (data: PropertyBrochureData): Promise<boolean> => {
      if (typeof window === "undefined") {
        console.warn("usePropertyBrochurePdf: hook ejecutado fuera del navegador.");
        return false;
      }

      if (!data?.title) {
        console.error(
          "usePropertyBrochurePdf: se requiere un título para generar el brochure."
        );
        return false;
      }

      const container = document.createElement("div");
      container.style.position = "fixed";
      container.style.left = "-10000px";
      container.style.top = "0";
      container.style.width = "210mm";
      container.style.zIndex = "-1";

      document.body.appendChild(container);

      const root = createRoot(container);

      let element: HTMLElement | null = null;
      let originalStyles:
        | {
            transform: string;
            transformOrigin: string;
            width: string;
            height: string;
          }
        | null = null;

      try {
        flushSync(() => {
          root.render(
            <Provider store={store}>
              <ThemeProvider theme={theme}>
                <PropertyBrochureTemplate data={data} />
              </ThemeProvider>
            </Provider>
          );
        });

        element = container.querySelector("#property-brochure") as HTMLElement | null;

        if (!element) {
          throw new Error("No se pudo crear la plantilla del brochure.");
        }

        await new Promise<void>(resolve => {
          requestAnimationFrame(() => resolve());
        });

        const pxToMm = (px: number) => (px * 25.4) / 96;
        const mmToPx = (mm: number) => (mm / 25.4) * 96;

        const pageHeightMm = 297;
        const pageWidthMm = 210;
        const pageHeightPx = mmToPx(pageHeightMm);

        const brochureHeightPx = element.scrollHeight;

        originalStyles = {
          transform: element.style.transform,
          transformOrigin: element.style.transformOrigin,
          width: element.style.width,
          height: element.style.height
        };

        if (brochureHeightPx > pageHeightPx) {
          const brochureHeightMm = pxToMm(brochureHeightPx);
          const scale = pageHeightMm / brochureHeightMm;

          element.style.transformOrigin = "top left";
          element.style.transform = `scale(${scale})`;
          element.style.width = `${pageWidthMm / scale}mm`;
          element.style.height = `${brochureHeightMm}mm`;
        }

        const safeName = data.title
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/^-+|-+$/g, "");

        const generatePdf = async (foreignObjectRendering = false) => {
          await html2pdf()
            .set({
              margin: [0, 0, 0, 0],
              filename: `brochure-${safeName || "propiedad"}.pdf`,
              image: { type: "jpeg", quality: 0.98 },
              html2canvas: {
                scale: 2,
                letterRendering: true,
                useCORS: true,
                backgroundColor: "#ffffff",
                foreignObjectRendering,
                onclone: (clonedDocument: Document) => {
                  sanitizeUnsupportedColorsInClone(clonedDocument);
                }
              },
              jsPDF: { unit: "mm", format: "a4", orientation: "portrait" }
            })
            .from(element)
            .save();
        };

        try {
          await generatePdf(false);
        } catch (error) {
          const message = error instanceof Error ? error.message : String(error);
          if (!message.toLowerCase().includes("oklch")) {
            throw error;
          }

          await generatePdf(true);
        }

        return true;
      } catch (error) {
        console.error("Error generando el brochure en PDF:", error);
        return false;
      } finally {
        if (element && originalStyles) {
          element.style.transform = originalStyles.transform;
          element.style.transformOrigin = originalStyles.transformOrigin;
          element.style.width = originalStyles.width;
          element.style.height = originalStyles.height;
        }

        root.unmount();
        document.body.removeChild(container);
      }
    },
    []
  );

  return { generateBrochure };
};

export default usePropertyBrochurePdf;
